/**
 * The Report Readiness ring, and the thin-criterion rule beside it.
 *
 * Authority: `um-app/04-umpire-timeline.md` §2.2 — *"The denominator is the number of criteria
 * in the active criteria set; the numerator is the number of criteria with at least one
 * released note. It measures the **evidence base for the report**, per umpire."* —
 * `um-app/06-report-studio.md` §3.1 (the same component at desktop scale) and §4.5 (*"The
 * thin-criterion warning fires for any criterion with fewer than two released notes"*).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE PROPERTIES, ALL OF THEM LOAD-BEARING
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **RELEASED NOTES ONLY, AND THE SIGNATURE SAYS SO.** The parameter is a list of criterion
 *     keys drawn from *released* notes; there is no visibility argument, no author argument and
 *     no second list for drafts or locked notes. The ring measures the **shared** evidence base
 *     — the evidence the panel and the Technical Delegate can also see — so a figure that moved
 *     when a private note was captured would be reporting something no reader could check. A
 *     manager's own locked notes are shown to her in the evidence pane and counted on the count
 *     line (`06` §3.2's `4 Yours Only`); they are deliberately not in this arithmetic.
 *
 *  2. **IT IS NOT A SCORE AND MUST NEVER BE LABELLED AS ONE** (`04` §6, Q-04b). `covered/total`
 *     counts criteria that have evidence, not criteria the umpire did well at. Nothing here
 *     weights, averages or ranks, and `MarkingCriterion.weight` is deliberately not read.
 *
 *  3. **DERIVED, NEVER STORED** (`types.ts` §9: *"report readiness — distinct
 *     `Note.criterion_stable_key` against the criteria set, computed across the WHOLE
 *     TOURNAMENT (ruling R1′)"*). Whoever calls this passes every released note for the umpire
 *     across both competitions; there is no competition parameter, because there is no
 *     per-competition readiness to compute.
 *
 * A note whose `criterion_stable_key` is `null`, or which names a key outside the active set,
 * contributes to **nothing** here. It is still real evidence and the evidence pane still shows
 * it, in its own ungrouped bucket — but it cannot cover a criterion it does not name, and
 * silently attributing it to one would inflate the ring.
 *
 * British English throughout, per the standing copy rule.
 */
/**
 * §4.5's threshold: *"fewer than two released notes"* is thin. Two, not one — a criterion
 * resting on a single observation is exactly the case the warning exists to make visible.
 */
export const THIN_CRITERION_MINIMUM = 2;
/**
 * The ring, the per-criterion counts and the thin list, from one pass over the released
 * evidence.
 *
 * `releasedCriterionKeys` is one entry **per released note** — repeats are the point, since a
 * criterion named by four notes must count four. Pass the keys of released notes and nothing
 * else; see property 1 in the module note.
 */
export function reportReadiness(criteria, releasedCriterionKeys) {
    const counts = new Map();
    for (const key of releasedCriterionKeys) {
        if (key === null)
            continue;
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const perCriterion = criteria.map((criterion) => ({
        criterionStableKey: criterion.criterion_stable_key,
        name: criterion.name,
        releasedCount: counts.get(criterion.criterion_stable_key) ?? 0,
    }));
    return {
        covered: perCriterion.filter((row) => row.releasedCount > 0).length,
        total: criteria.length,
        perCriterion,
        thin: perCriterion.filter((row) => row.releasedCount < THIN_CRITERION_MINIMUM),
    };
}
/** The ring's centred mono value, e.g. `5/10`. One spelling, used by every surface. */
export function formatReadiness(readiness) {
    return `${String(readiness.covered)}/${String(readiness.total)}`;
}
/**
 * The fraction of the ring's arc that is filled, `0`–`1`.
 *
 * An empty criteria set yields `0` rather than `NaN`: a ring with no denominator is drawn
 * empty and the surface says why in words, exactly as the progress bar does at §A3.
 */
export function readinessFraction(readiness) {
    if (readiness.total <= 0)
        return 0;
    return Math.min(1, Math.max(0, readiness.covered / readiness.total));
}
//# sourceMappingURL=report-readiness.js.map